import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchFromApi } from '../apiClient';
import {
  CompanyUsers,
  CreateNewUserPayload,
  EditUserPayload,
  UploadUserAvatarResponse,
  UserByWallet,
  UserCompanyList,
  UsersResponse,
} from 'types/userTypes';
import { useWeb3 } from 'contexts/Web3Context';
import { UserPermissionsPerCompanyResponse } from 'types/rolesTypes';
import { PaginationQueryParams } from 'types/queryTypes';
import { useUI } from 'contexts/UiContext';
import { SortOrder } from 'hooks/useTableControls';

const REACT_APP_DARWIN_URL = process.env.REACT_APP_DARWIN_URL;

// Query key
const USER_QUERY_KEY = 'user';
const USER_VALIDATION_EMAIL_KEY = 'user_validation_email_key';
const USER_GET_UATHORIEZ_COMPANIES_QUERY_KEY = 'user_get_authorize_companies';
const USERS_QUERY_KEY = 'users';
const ALL_USER_BY_COMPANY_QUERY_KEY = 'all_user_by_company';
const USER_PERMISSIONS_PER_COMPANY_QUERY_KEY = 'user_permissions_per_company';
const USER_BY_ID_QUERY_KEY = 'user_by_id';

const SUPPORTED_LANGUAGES = ['en', 'es', 'pt'];

/**
 * Fetch user all the users by by-company.
 */
export const useGetUsersByCompany = () => {
  const { selectedCompany } = useUI();
  return useQuery({
    queryKey: [ALL_USER_BY_COMPANY_QUERY_KEY],
    queryFn: () =>
      fetchFromApi<CompanyUsers[]>(
        `${REACT_APP_DARWIN_URL}/users/${selectedCompany}/by-company`
      ),
  });
};

export const useGetUserByAllCompanies = (processId: string) => {
  const { selectedCompany } = useUI();
  return useQuery({
    queryKey: [ALL_USER_BY_COMPANY_QUERY_KEY],
    queryFn: () =>
      fetchFromApi<CompanyUsers[]>(
        `${REACT_APP_DARWIN_URL}/users/${selectedCompany}/by-account/${processId}`
      ),
  });
};

/**
 * Fetch user information logged user.
 */
export const useGetLoggedUser = (options?: { enabled: boolean }) => {
  const { address: wallet } = useWeb3();
  return useQuery({
    queryKey: [USER_QUERY_KEY, wallet],
    queryFn: () =>
      fetchFromApi<UserByWallet>(`${REACT_APP_DARWIN_URL}/users/me`),
    enabled: !!wallet && (options?.enabled ?? true),
  });
};

/**
 * Fetch companies associated the logged user.
 */
export const useGetUserCompanies = (options?: {
  enabled: boolean;
  isPrimary?: boolean; // if true, only returns the primary company of the user ( used for demo now ).
}) => {
  const { address: wallet } = useWeb3();

  return useQuery({
    queryKey: [USER_GET_UATHORIEZ_COMPANIES_QUERY_KEY, wallet],
    queryFn: () =>
      fetchFromApi<{ companies: UserCompanyList }>(
        `${REACT_APP_DARWIN_URL}/users/my-companies?isPrimary=${options?.isPrimary ?? false}`
      ),
    enabled: !!wallet && (options?.enabled ?? true),
  });
};

/**
 * Validate email user before login.
 */
export const useValidateUserEmail = ({ email }: { email: string }) => {
  return useQuery({
    queryKey: [USER_VALIDATION_EMAIL_KEY],
    queryFn: () =>
      fetchFromApi(`${REACT_APP_DARWIN_URL}/users/validate/${email}`),
    enabled: false,
  });
};

/**
 * Pre-generate user wallet based on email.
 */
export const pregenerateUserWallet = async (email: string): Promise<string> => {
  const usePolygon = process.env.REACT_APP_USE_CHAIN === 'polygon';

  const clientId = usePolygon
    ? (process.env.REACT_APP_WEB3_AUTH_CLIENT_ID_PROD as string)
    : (process.env.REACT_APP_WEB3_AUTH_CLIENT_ID_DEV as string);
  const verifier = usePolygon
    ? (process.env.REACT_APP_WEB3_AUTH_CLIENT_VERIFIER_PROD as string)
    : (process.env.REACT_APP_WEB3_AUTH_CLIENT_VERIFIER_DEV as string);
  const net = usePolygon ? 'sapphire_mainnet' : 'sapphire_devnet';

  const url = `https://lookup.web3auth.io/lookup?verifier=${encodeURIComponent(
    verifier
  )}&verifierId=${encodeURIComponent(
    email
  )}&web3AuthNetwork=${encodeURIComponent(net)}&clientId=${encodeURIComponent(
    clientId
  )}&`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Error: ${response.status} ${response.statusText}`);
  }

  const res = await response.json();
  return res.data.walletAddress;
};

/**
 * Fetch user permissions within a company.
 */
export const useGetUserPermissionsPerCompany = (
  companyId: string,
  options?: { enabled: boolean }
) => {
  return useQuery({
    queryKey: [USER_PERMISSIONS_PER_COMPANY_QUERY_KEY, companyId],
    queryFn: () =>
      fetchFromApi<UserPermissionsPerCompanyResponse>(
        `${REACT_APP_DARWIN_URL}/companies/${companyId}/permissions/my-permissions`
      ),
    enabled: !!companyId && (options?.enabled ?? true),
  });
};

/**
 * Validate if email+organization relation exists
 */
export const useValidateEmailAndOrganizationRelation = (
  email: string,
  organization: string
) => {
  return useQuery({
    queryKey: [USER_PERMISSIONS_PER_COMPANY_QUERY_KEY, email, organization],
    queryFn: () =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/users/validate/${email}/${organization}`
      ),
    enabled: false,
  });
};

/**
 * Create a new user.
 */
export const useCreateNewUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userData,
      companyId,
    }: {
      userData: CreateNewUserPayload;
      companyId: string | number;
    }) =>
      fetchFromApi(`${REACT_APP_DARWIN_URL}/users/company-roles/${companyId}`, {
        method: 'POST',
        body: JSON.stringify(userData),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY] });
    },
  });
};

/**
 * Fetch user list.
 *
 */
export const useGetUserList = ({
  page = 1,
  limit = 10,
  query = '',
  sortOrder = { sortBy: 'name', order: 'ASC' },
}: PaginationQueryParams & {
  sortOrder: SortOrder;
}) => {
  const { selectedCompany } = useUI();

  return useQuery({
    queryKey: [USERS_QUERY_KEY, limit, page, query, sortOrder],
    queryFn: () =>
      fetchFromApi<UsersResponse>(
        `${REACT_APP_DARWIN_URL}/companies/${selectedCompany}/users?page=${page}&limit=${limit}&filter=${query}&sortBy=${sortOrder.sortBy}&order=${sortOrder.order}`
      ),
    refetchOnWindowFocus: false,
  });
};

/**
 * Delete a user.
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      userCompanyRoleId,
    }: {
      companyId: string;
      userCompanyRoleId: string;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/users/company-roles/${companyId}/${userCompanyRoleId}`,
        {
          method: 'DELETE',
        }
      ),
    onSuccess: () => {
      // Invalidate the cache of the user list after deleting an user
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
};

/**
 * Get user by id.
 */
export const useGetUserById = (
  companyId: string,
  userCompanyRoleId: string
) => {
  return useQuery({
    queryKey: [USER_BY_ID_QUERY_KEY, companyId, userCompanyRoleId],
    queryFn: () =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/users/user-company/${companyId}/${userCompanyRoleId}`
      ),
    enabled: !!companyId && !!userCompanyRoleId,
  });
};

/**
 * Upload user image (avatar)
 */
export const useUploadAvatarFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      folder = 'users',
    }: {
      file: File;
      folder?: string;
    }) => {
      const formData = new FormData();
      formData.append('file', file);

      return fetchFromApi<UploadUserAvatarResponse>(
        `${REACT_APP_DARWIN_URL}/files/upload/${folder}`,
        {
          method: 'POST',
          body: formData,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY] });
    },
  });
};

/**
 * Activate or deactivate an user.
 */
export const useActivateDeactivateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      userCompanyRoleId,
      payload,
    }: {
      companyId: string;
      userCompanyRoleId: string;
      payload: { active: boolean };
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/users/company-roles/${companyId}/${userCompanyRoleId}/active`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      // Invalidate the cache of the user list after activating/deactivating an user
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
};

/**
 * Edit an user.
 */
export const useEditUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userData,
      companyId,
      userCompanyRoleId,
    }: {
      userData: EditUserPayload;
      companyId: string | number;
      userCompanyRoleId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/users/company-roles/${companyId}/${userCompanyRoleId}`,
        {
          method: 'PUT',
          body: JSON.stringify(userData),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY] });
    },
  });
};

/**
 * Change language for user.
 */
export const useChangeUserLanguage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ languageData }: { languageData: { language: string } }) => {
      if (!SUPPORTED_LANGUAGES.includes(languageData.language)) {
        throw new Error(
          `Invalid language code: ${
            languageData.language
          }. Supported languages are: ${SUPPORTED_LANGUAGES.join(', ')}`
        );
      }
      return fetchFromApi(`${REACT_APP_DARWIN_URL}/users/language`, {
        method: 'PUT',
        body: JSON.stringify(languageData),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_BY_ID_QUERY_KEY] });
    },
  });
};
